import React, { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, X, Sparkles, GraduationCap, ArrowRight, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Public "Try a sample lesson" preview.
 * A short real excerpt from Chapter 1 + 5 real quiz questions with instant
 * feedback and explanations — so prospects can feel the program before buying.
 */

const READING = {
  chapter: "Chapter 1 · Tax Fundamentals & Filing Status",
  section: "Section 1.3 · Filing Status Decision Tree",
  paragraphs: [
    "Filing status is the single biggest lever on a return. It changes the standard deduction, the tax brackets, the phaseout ranges for almost every credit, and whether the taxpayer even qualifies for benefits like the Earned Income Credit. Get this wrong and the entire return is wrong — even if every number after it is perfect.",
    "The five statuses (Single, MFJ, MFS, HOH, and Qualifying Surviving Spouse) are not chosen freely — the IRS assigns them by rule. Your job as a preparer is to walk down the decision tree in order: marital status on 12/31, then dependents, then who paid more than half the household costs, then the tie-breakers when parents live apart.",
    "Head of Household (HOH) is the most-abused status in the field. Every preparer will see clients try to claim it because 'a friend told them to.' To qualify: the taxpayer must be UNMARRIED (or considered unmarried) on the last day of the year, must have paid more than half the cost of keeping up a home, and must have a qualifying person live with them for more than half the year (a qualifying child, or a qualifying relative dependent). Skipping any one of those three tests is what triggers due-diligence penalties on the preparer.",
  ],
  keyFact: "Due-diligence records must be retained for 3 years from the later of the return due date or the date the return was filed. Miss it, and you owe $600 per return under §6695(g).",
};

const QUESTIONS = [
  {
    q: "Which filing status is almost always the WORST for a married couple?",
    options: ["MFJ", "MFS", "HOH", "Qualifying Surviving Spouse"],
    answer: 1,
    explain: "MFS (Married Filing Separately) disqualifies most refundable credits — no EIC, no education credits, no student loan interest, and a phased-out CTC. Only a handful of narrow situations (like income-based student loan repayment plans) make it worth choosing.",
  },
  {
    q: "For a Qualifying Child, the child must NOT provide more than what portion of their own support?",
    options: ["25%", "One-third", "Half", "80%"],
    answer: 2,
    explain: "The child cannot provide more than HALF of their own support. Notice the direction — this is different from the Qualifying Relative test (where the taxpayer must provide more than half).",
  },
  {
    q: "Ana is unmarried on 12/31 and pays 100% of home costs for her daughter (a Qualifying Child). What filing status?",
    options: ["Single", "MFJ", "Head of Household", "Qualifying Surviving Spouse"],
    answer: 2,
    explain: "HOH — she checks all three boxes: unmarried on 12/31, paid >½ costs of keeping up the home, and had a qualifying person (her QC daughter) live with her for >½ the year. This is the highest standard deduction of any single-filer status.",
  },
  {
    q: "AGI (Adjusted Gross Income) appears on which line of Form 1040?",
    options: ["Line 9", "Line 11", "Line 15", "Line 24"],
    answer: 1,
    explain: "Line 11. Total income lives on Line 9, above-the-line adjustments come off on Line 10, and Line 11 gives you AGI — the gatekeeper number for most phaseouts and deduction limits.",
  },
  {
    q: "Due-diligence records must be kept for:",
    options: ["1 year", "3 years", "6 years", "10 years"],
    answer: 1,
    explain: "3 years from the later of the due date or the filing date. Includes Form 8867, worksheets, notes on the knowledge requirement, and copies of client-provided docs. This is what protects you when the IRS sends a §6695(g) preparer penalty letter.",
  },
];

function Question({ q, index, total, onAnswered }) {
  const [picked, setPicked] = useState(null);
  const submitted = picked !== null;
  const correct = submitted && picked === q.answer;

  return (
    <Card className="p-6 md:p-8 space-y-5" data-testid={`sample-q-${index}`}>
      <div className="flex items-center justify-between">
        <div className="label-caps text-primary">Question {index + 1} of {total}</div>
        <div className="text-xs text-muted-foreground">Sample from Chapter 1</div>
      </div>
      <div className="font-serif text-xl md:text-2xl leading-snug">{q.q}</div>
      <div className="space-y-2">
        {q.options.map((opt, i) => {
          const isPicked = picked === i;
          const isRight = i === q.answer;
          const state = !submitted
            ? "default"
            : isRight
              ? "right"
              : isPicked
                ? "wrong"
                : "muted";
          return (
            <button
              key={i}
              type="button"
              data-testid={`sample-q-${index}-opt-${i}`}
              disabled={submitted}
              onClick={() => setPicked(i)}
              className={cn(
                "w-full text-left rounded-md border p-3 md:p-4 text-sm md:text-base flex items-start gap-3 transition-colors",
                state === "default" && "hover:bg-accent/5 border-border",
                state === "right" && "bg-emerald-50 border-emerald-500 text-emerald-900",
                state === "wrong" && "bg-red-50 border-red-500 text-red-900",
                state === "muted" && "opacity-60 border-border",
              )}
            >
              <span className={cn(
                "flex-none inline-flex items-center justify-center h-5 w-5 rounded-full border text-[11px] font-semibold mt-0.5",
                state === "right" && "bg-emerald-500 border-emerald-500 text-white",
                state === "wrong" && "bg-red-500 border-red-500 text-white",
                state === "default" && "border-muted-foreground text-muted-foreground",
                state === "muted" && "border-muted-foreground text-muted-foreground",
              )}>
                {state === "right" ? <Check size={12} /> : state === "wrong" ? <X size={12} /> : String.fromCharCode(65 + i)}
              </span>
              <span>{opt}</span>
            </button>
          );
        })}
      </div>
      {submitted && (
        <div
          className={cn(
            "rounded-md border p-4 text-sm",
            correct ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900",
          )}
          data-testid={`sample-q-${index}-explain`}
        >
          <div className="label-caps mb-1">
            {correct ? "Nice — that's right" : "Not quite — here's why"}
          </div>
          <div className="leading-relaxed">{q.explain}</div>
          <div className="mt-3">
            <Button
              data-testid={`sample-q-${index}-next`}
              size="sm"
              onClick={() => onAnswered(correct)}
            >
              {index === total - 1 ? "See my results" : "Next question"} <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function Preview() {
  const [step, setStep] = useState("reading"); // reading → quiz → done
  const [qIndex, setQIndex] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);

  const handleAnswered = (wasCorrect) => {
    if (wasCorrect) setCorrectCount((c) => c + 1);
    if (qIndex < QUESTIONS.length - 1) {
      setQIndex((i) => i + 1);
    } else {
      setStep("done");
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14" data-testid="preview-page">
        {/* Header */}
        <div className="mb-8">
          <div className="label-caps text-primary flex items-center gap-2">
            <Sparkles size={14} /> Free sample · No sign-up
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-none">
            Try a lesson.
            <span className="block text-primary italic">See how it feels.</span>
          </h1>
          <p className="text-muted-foreground mt-4 max-w-xl">
            Read a real passage from Chapter 1, then answer 5 real quiz questions —
            the same style, tone, and explanations you'll get across all 13 chapters.
          </p>
        </div>

        {step === "reading" && (
          <div className="space-y-6">
            <Card className="p-6 md:p-8 space-y-4" data-testid="preview-reading">
              <div className="flex items-center gap-2 text-primary">
                <BookOpen size={14} /> <span className="label-caps text-[11px]">{READING.chapter}</span>
              </div>
              <div className="font-serif text-2xl md:text-3xl">{READING.section}</div>
              <div className="space-y-4 text-sm md:text-base leading-relaxed text-foreground/90">
                {READING.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <div className="mt-2 rounded-md border-l-4 border-primary bg-primary/5 p-4 text-sm">
                <div className="label-caps text-primary mb-1">Key fact</div>
                <div>{READING.keyFact}</div>
              </div>
            </Card>
            <div className="flex flex-wrap gap-3 justify-between items-center">
              <div className="text-xs text-muted-foreground">
                About 90 seconds of reading · followed by a short check-your-understanding.
              </div>
              <Button
                data-testid="preview-start-quiz"
                onClick={() => setStep("quiz")}
                className="min-w-[180px]"
              >
                Try the quiz <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === "quiz" && (
          <Question
            key={qIndex}
            q={QUESTIONS[qIndex]}
            index={qIndex}
            total={QUESTIONS.length}
            onAnswered={handleAnswered}
          />
        )}

        {step === "done" && (
          <div className="space-y-6" data-testid="preview-results">
            <Card className="p-8 md:p-10 text-center space-y-3">
              <div className="label-caps text-primary flex items-center justify-center gap-2">
                <GraduationCap size={14} /> Preview complete
              </div>
              <div className="font-serif text-4xl md:text-5xl">
                {correctCount} <span className="text-muted-foreground">/ {QUESTIONS.length}</span>
              </div>
              <div className="text-muted-foreground max-w-md mx-auto text-sm md:text-base">
                {correctCount === QUESTIONS.length
                  ? "Perfect — you're going to fly through this course. Enrollment unlocks all 13 chapters, 4 practice returns, and your certificate."
                  : correctCount >= 3
                    ? "Nice work — most first-timers land in this range. The full course walks every rule with real client examples so the missed ones become second nature."
                    : "That's totally normal for a cold start — every rule you missed has its own dedicated section, examples, and a mid-chapter checkpoint like the one you just took."}
              </div>
            </Card>

            <Card className="p-6 md:p-8 space-y-4" data-testid="preview-cta">
              <div className="font-serif text-2xl md:text-3xl">Ready to go all-in?</div>
              <ul className="text-sm md:text-base text-muted-foreground space-y-2 list-disc pl-5">
                <li>13 full chapters — every line of Form 1040 explained in plain English</li>
                <li>Mid-chapter checkpoints (like the one you just tried) so nothing slips</li>
                <li>4 guided 1040 practice returns using real client-style docs</li>
                <li>25-question final exam · 80% to pass · downloadable certificate</li>
                <li>Ask a real human instructor when you get stuck</li>
              </ul>
              <div className="flex flex-wrap gap-3 pt-2">
                <Button asChild className="min-w-[180px]" data-testid="preview-cta-enroll">
                  <Link to="/pricing">See course plans <ArrowRight size={16} className="ml-1" /></Link>
                </Button>
                <Button asChild variant="outline" data-testid="preview-cta-retry" onClick={() => { setStep("reading"); setQIndex(0); setCorrectCount(0); }}>
                  <span>Try the sample again</span>
                </Button>
                <Button asChild variant="ghost" data-testid="preview-cta-file">
                  <Link to="/services">Or have us file your return →</Link>
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
